// The drawing vocabulary of an A01 "plan de situație", as the reference sheet
// (Bd. Timișoara 12-14, S.C. SONEX DESIGN) draws it.
//
// Everything here is pure drawing in DRAWING METRES, and everything here has to
// survive the PDF export path, which hands the SVG to svg2pdf.js 2.7 with a
// metre viewBox. That library implements less of SVG than a browser, and the
// gaps are not obvious, so they are worth naming once:
//
//   * `vector-effect="non-scaling-stroke"` is NOT implemented. A `strokeWidth`
//     of 1 is one METRE of ink on the sheet, not one pixel. Every width below is
//     a real dimension.
//   * `dominant-baseline` is NOT implemented, so vertical centring has to be an
//     explicit `dy`. `CAP` is that offset.
//   * `paint-order` is NOT implemented, so the "stroke a white halo under the
//     fill" trick prints as a fat outline over the glyph. Text that has to read
//     over a coloured surface gets a real `<rect>` plate behind it instead —
//     which is what the reference draws anyway.
//   * `<mask>` is not supported; `clipPath`, `pattern`, `transform`, `opacity`,
//     `fill-rule`, `stroke-dasharray` and `<tspan>` are.

import { createContext, useContext, useId } from "react";
import { VERDE_SOL, VERDE_SUBSOL } from "./verdeLot";
import { culoareMasina, liniiMasina } from "./masiniLot";
import { caleHasura, liniiHasuraMulti } from "./hasura";
import { DECOR, SEL_STROKE, type Pickable } from "./glyphs";
import { pathAt, SITE_STALL_W, type Crossing, type DimSpan, type RoadMark, type SiteRamp,
         type SiteSign, type SiteStall, type StallRun, type Tree,
         type Vertex } from "./siteDerive";
import { t } from "../../i18n";
import type { Pt } from "../types";
import type { CotaCorp } from "./coteSit";

/** The A01 palette, sampled from the reference sheet itself rather than
 *  invented: a colour census over the rendered page puts 96% of its pixels on
 *  these nine values. They are deliberately NOT the viewer's `--paper`/`--ink`
 *  theme tokens — this layer reproduces a specific printed drawing, and the
 *  theme exists to restyle the interactive plans, not this one. */
/** Verdele coroanei pe planșă: mai deschis decât verdele pe sol, ca să stea peste el. */
const VERDE_COROANA = "#9ed98a";
/** Verdele închis al conturului și al crengilor, ca pe planșa de urbanism. */
const VERDE_CREANGA = "#2f7a3a";

export const A01 = {
  paper: "#ffffff",       // building fill, sign plates
  paved: "#f0f0f0",       // umplutura CLĂDIRII (v. `BarView`) și pridvorul ei
  // TROTUARUL, gri deschis limpede.
  //
  // Era tot `paved`, adică EXACT culoarea clădirii — deci banda pietonală din
  // jurul unui corp și corpul însuși se citeau ca o singură suprafață albă, iar
  // conturul clădirii era tot ce le despărțea. Un ton propriu le separă fără să
  // atragă atenția: mai închis decât clădirea, dar mai deschis decât carosabilul
  // (`road`), ca ierarhia «clădire → pietonal → carosabil → parcare» să se
  // citească în ordine, de la deschis la închis.
  walk: "#cdcdcd",        // trotuare, alei pietonale
  road: "#bfbfbf",        // carriageway and drive aisle
  apron: "#7c7c7c",       // parking asphalt
  deep: VERDE_SOL,        // spațiu verde adânc — planted on real ground (= macheta 3D)
  slab: VERDE_SUBSOL,     // verde peste subsol (= macheta 3D)
  paver: "#e2e2e2",       // the running-bond joint lines
  street: "#d597d6",      // the public road outside the property
  red: "#fe0000",         // property line, access marks, dimensions
  signRed: "#d8261a",
  signBlue: "#035086",
  ink: "#1a1a1a",
  hair: "#8a8a8a",
} as const;

export const SANS = "Helvetica, Arial, sans-serif";

/** How far the DRAWING is turned on screen, in degrees.
 *
 *  The viewer rotates the whole plan with one transform, which turns the
 *  lettering with it — past a quarter turn every label reads upside down. Text
 *  cancels this angle out, so the annotation keeps the orientation it was
 *  designed with at rest: a bar label stays horizontal, a dimension value stays
 *  along its own chain, and neither of them cares which way the plan is facing.
 *
 *  A context rather than a prop because it has to reach a dozen glyphs several
 *  levels down, and every one of them would otherwise be threading a number it
 *  does nothing with. Zero on the plotted sheet, which never turns. */
export const ViewTurn = createContext(0);
export const useViewTurn = () => useContext(ViewTurn);

/** Vertical centring for a `fontSize` of `fs`, standing in for the
 *  `dominant-baseline: central` the exporter drops. */
export const CAP = (fs: number) => fs * 0.36;

// --------------------------------------------------------------------------- //
// text on a plate
// --------------------------------------------------------------------------- //

/** A value on its own white plate — the reference's way of keeping a dimension
 *  or an "acces" caption readable over asphalt, and the only way that survives
 *  the export (see the `paint-order` note above). */
export function Plate({ x, y, text, fs = 1.0, fill = A01.red, deg = 0,
                       weight = 700, lines }:
  { x: number; y: number; text?: string; fs?: number; fill?: string;
    deg?: number; weight?: number; lines?: string[] }) {
  const rows = lines ?? [text ?? ""];
  const turn = useViewTurn();
  const w = Math.max(...rows.map((r) => r.length)) * fs * 0.60 + fs * 0.4;
  const h = rows.length * fs * 1.18 + fs * 0.18;
  return (
    <g transform={`translate(${x} ${y}) rotate(${deg - turn})`} {...DECOR}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} fill="#ffffff" />
      {rows.map((r, i) => (
        <text key={i} x={0} y={-h / 2 + fs * 0.18 + fs * 1.18 * i + fs * 0.82}
          fontSize={fs} fontFamily={SANS} fontWeight={weight}
          textAnchor="middle" fill={fill}>{r}</text>
      ))}
    </g>
  );
}

// --------------------------------------------------------------------------- //
// planting
// --------------------------------------------------------------------------- //

/** Un zar determinist mic, pentru formele unui pom: din `rot`, ca același pom
 *  să iasă la fel la fiecare desen (planșa și PDF-ul trebuie să coincidă). */
function zarPom(seed: number) {
  let x = (Math.floor(seed * 1000) % 2147483647) || 1;
  return () => (x = (x * 48271) % 2147483647) / 2147483647;
}

/** Un pom, cum îl desenează planșa de urbanism (modelul: PUD-ul primit de la
 *  Oana, 31.10.2026): coroana ca un NOR de lobi neregulați cu umplere verde
 *  străvezie și, înăuntru, ARBORELE VĂZUT DE SUS — trunchiul în centru, crengile
 *  principale care se ramifică de două ori spre marginea coroanei, tot mai
 *  subțiri. Umplerea e străvezie ca verdele și locurile de sub coroană să se
 *  citească prin ea: pomul din fâșia parcării stă cu coroana peste spatele
 *  locurilor, exact ca pe planșă. Fără umbră: planșa nu o are.
 *
 *  Prima variantă (raze drepte până la contur) se citea ca o umbrelă, nu ca un
 *  copac (Oana, 31.10.2026): un copac are crengi care se despart. */
export function TreeCanopy({ t }: { t: Tree }) {
  const zar = zarPom(t.rot + t.blades);
  const P = (x: number, y: number) => `${x.toFixed(3)} ${y.toFixed(3)}`;
  // CONTURUL: lobi convecși (arce) între vârfuri la rază ușor variabilă
  const lobi = t.blades * 2;
  const sec = (2 * Math.PI) / lobi;
  const varfuri: [number, number][] = [];
  for (let k = 0; k < lobi; k++) {
    const a = k * sec + (zar() - 0.5) * sec * 0.3;
    const rr = t.r * (0.84 + zar() * 0.12);
    varfuri.push([Math.cos(a) * rr, Math.sin(a) * rr]);
  }
  let contur = `M${P(varfuri[0][0], varfuri[0][1])}`;
  for (let k = 0; k < lobi; k++) {
    const [x0, y0] = varfuri[k], [x1, y1] = varfuri[(k + 1) % lobi];
    const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
    const lm = Math.hypot(mx, my) || 1;
    // controlul lobului, în afara coardei: lobul bombează spre exterior
    const bomb = t.r * (1.04 + zar() * 0.08);
    contur += `Q${P((mx / lm) * bomb, (my / lm) * bomb)} ${P(x1, y1)}`;
  }
  contur += "Z";
  // CRENGILE: 4–6 principale din trunchi, fiecare se desparte de două ori;
  // o creangă nu trece de 0,92 r (rămâne sub coroană)
  const nivele: string[][] = [[], [], []];
  const creanga = (x: number, y: number, ang: number, lung: number, nivel: number) => {
    const x1 = x + Math.cos(ang) * lung, y1 = y + Math.sin(ang) * lung;
    if (Math.hypot(x1, y1) > t.r * 0.92) return;
    // ușor arcuită, cu controlul abătut perpendicular
    const cx = (x + x1) / 2 - Math.sin(ang) * lung * (zar() - 0.5) * 0.3;
    const cy = (y + y1) / 2 + Math.cos(ang) * lung * (zar() - 0.5) * 0.3;
    nivele[nivel].push(`M${P(x, y)}Q${P(cx, cy)} ${P(x1, y1)}`);
    if (nivel === 2) return;
    const n = nivel === 0 ? 2 : (zar() < 0.6 ? 2 : 1);
    for (let i = 0; i < n; i++) {
      const dev = (n === 1 ? (zar() - 0.5) * 0.6 : (i === 0 ? -1 : 1) * (0.35 + zar() * 0.35));
      creanga(x1, y1, ang + dev, lung * (0.62 + zar() * 0.2), nivel + 1);
    }
  };
  const principale = 4 + Math.floor(zar() * 3);
  for (let i = 0; i < principale; i++) {
    const ang = (i / principale) * 2 * Math.PI + (zar() - 0.5) * 0.5;
    creanga(0, 0, ang, t.r * (0.36 + zar() * 0.12), 0);
  }
  const sw = Math.max(0.03, t.r * 0.028);
  return (
    <g transform={`translate(${t.x} ${t.y}) rotate(${t.rot})`} {...DECOR}>
      <path d={contur} fill={VERDE_COROANA} fillOpacity={0.5}
        stroke={VERDE_CREANGA} strokeWidth={sw} strokeLinejoin="round" />
      <g fill="none" stroke={VERDE_CREANGA} strokeLinecap="round">
        <path d={nivele[0].join("")} strokeWidth={sw * 1.8} />
        <path d={nivele[1].join("")} strokeWidth={sw * 1.1} />
        <path d={nivele[2].join("")} strokeWidth={sw * 0.6} />
      </g>
      <circle cx={0} cy={0} r={Math.max(0.06, t.r * 0.08)} fill={VERDE_CREANGA} />
    </g>
  );
}

// --------------------------------------------------------------------------- //
// parking
// --------------------------------------------------------------------------- //

/** The asphalt pad under one row of stalls, with the divider lines painted on
 *  it. The reference draws the pad as ONE continuous surface with rounded ends
 *  and lets the planting breaks show through it — which is why this is a single
 *  rectangle per run and the green is re-drawn over it, rather than a rectangle
 *  per stall. */
export function ApronPad({ run }: { run: StallRun }) {
  const w = run.w, h = run.h;
  // COLȚURI DREPTE (Oana, 31.10.2026): un loc de parcare n-are colțuri rotunjite, iar sub rândul
  // rotunjit se vedea, în fiecare colț, asfaltul deschis al pavajului de dedesubt (desenat drept).
  return (
    <g transform={`translate(${run.cx} ${run.cy}) rotate(${run.deg})`} {...DECOR}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h}
        fill={A01.apron} />
    </g>
  );
}

/** The stall dividers, drawn per run so they land exactly on the pitch the row
 *  was tiled at.
 *
 *  DARK on the asphalt, not white. White was a guess from a crop where the pale
 *  cars nearly filled their bays and the gaps between them read as paint; on the
 *  sheet the bay outline is a thin dark line and the asphalt shows between. */
export function ApronLines({ run }: { run: StallRun }) {
  const w = run.w, h = run.h;
  const edges = run.cuts
    .map((c, i) => c + (run.stalls[i]?.w ?? 2.5) / 2)
    .filter((c) => c > 0.2 && c < w - 0.2);
  return (
    <g transform={`translate(${run.cx} ${run.cy}) rotate(${run.deg})`} {...DECOR}>
      {edges.map((c, i) => (
        <line key={i} x1={-w / 2 + c} y1={-h / 2} x2={-w / 2 + c} y2={h / 2}
          stroke="#4a4a4a" strokeWidth={0.1} />
      ))}
      <rect x={-w / 2} y={-h / 2} width={w} height={h}
        fill="none" stroke="#4a4a4a" strokeWidth={0.1} />
    </g>
  );
}

/** A car in plan, drawn rather than traced.
 *
 *  `#pg-car` is a potrace of a line drawing: 7.5 KB of path with no fill, which
 *  is beautiful at 40 px/m and INVISIBLE at 6, where its strokes go sub-pixel
 *  and all that survives is whatever sits underneath it. Underneath used to be a
 *  rounded rectangle, and a row of rounded rectangles reads as paving slabs, not
 *  as parked cars.
 *
 *  So the silhouette is a real shape: a tapered body, a cabin, a windscreen and
 *  a rear window, with mirrors. It carries the drawing on its own at any size,
 *  and the traced art still goes over it for the fine detail when there is room
 *  to see it. Nose toward -y, the same convention as the symbol. */
function CarBody({ w, d, ada, fill }: { w: number; d: number; ada?: boolean; fill: string }) {
  const hw = w / 2, hd = d / 2;
  const body =
    `M${-hw * 0.90} ${-hd * 0.62}` +
    `C${-hw * 0.90} ${-hd * 0.92} ${-hw * 0.52} ${-hd} ${0} ${-hd}` +
    `C${hw * 0.52} ${-hd} ${hw * 0.90} ${-hd * 0.92} ${hw * 0.90} ${-hd * 0.62}` +
    `L${hw} ${hd * 0.30}` +
    `C${hw} ${hd * 0.88} ${hw * 0.62} ${hd} ${0} ${hd}` +
    `C${-hw * 0.62} ${hd} ${-hw} ${hd * 0.88} ${-hw} ${hd * 0.30}` +
    `Z`;
  // linii gri, nu negre (v. `masiniLot.liniiMasina`); locul accesibil își
  // păstrează albastrul, care spune ceva, nu doar decorează
  const { contur: line, geam } = liniiMasina(fill);
  const sw = Math.max(0.03, w * 0.024);
  return (
    <g>
      <path d={body} fill={ada ? "#dfe9f4" : fill}
        stroke={line} strokeWidth={sw} strokeLinejoin="round" />
      {/* cabin: windscreen at the front, rear window behind */}
      <path d={`M${-hw * 0.74} ${-hd * 0.20}L${-hw * 0.60} ${-hd * 0.52}
                L${hw * 0.60} ${-hd * 0.52}L${hw * 0.74} ${-hd * 0.20}Z`}
        fill="none" stroke={geam} strokeWidth={sw * 0.8} strokeLinejoin="round" />
      <path d={`M${-hw * 0.78} ${hd * 0.30}L${-hw * 0.64} ${hd * 0.62}
                L${hw * 0.64} ${hd * 0.62}L${hw * 0.78} ${hd * 0.30}Z`}
        fill="none" stroke={geam} strokeWidth={sw * 0.8} strokeLinejoin="round" />
      {/* mirrors */}
      <path d={`M${-hw} ${-hd * 0.16}l${-w * 0.09} ${d * 0.02}
                M${hw} ${-hd * 0.16}l${w * 0.09} ${d * 0.02}`}
        stroke={line} strokeWidth={sw * 1.4} strokeLinecap="round" />
    </g>
  );
}

/** Fâșia de transfer a locului PMR, în reperul locului: dungi diagonale subțiri
 *  în culoarea semnelor, la 45°, pe `x0..x1` × toată adâncimea — hașura
 *  marcajului rutier, nu o umplutură (ar fi a doua suprafață pe loc). Liniile se
 *  calculează direct (nu un `pattern`), ca glifa să rămână de sine stătătoare și
 *  să iasă la fel în PDF. */
function FasiePmr({ x0, x1, D }: { x0: number; x1: number; D: number }) {
  const pas = 0.35, y0 = -D / 2, y1 = D / 2;
  const linii: string[] = [];
  // liniile x − y = c, tăiate de dreptunghi
  for (let c = x0 - y1; c <= x1 - y0; c += pas) {
    const ya = Math.max(y0, x0 - c), yb = Math.min(y1, x1 - c);
    if (yb - ya > 1e-6) linii.push(`M${(ya + c).toFixed(3)} ${ya.toFixed(3)}L${(yb + c).toFixed(3)} ${yb.toFixed(3)}`);
  }
  return (
    <g>
      <rect x={x0} y={y0} width={x1 - x0} height={D} fill="none"
        stroke={A01.signBlue} strokeWidth={0.05} />
      <path d={linii.join("")} stroke={A01.signBlue} strokeWidth={0.05} fill="none" />
    </g>
  );
}

/** Aceeași fâșie PMR, pe un poligon oarecare din planșă: banda comună
 *  (`AccessibleAisle` de la sol, `SiteLayer.pmrStrips`), care stă lângă locul
 *  `ada` de 2,50 m, nu în el — deci nu e în reperul niciunui loc. Aceleași
 *  dungi la 45° și același pas ca `FasiePmr`, prin `liniiHasuraMulti`, într-un
 *  singur `path`. `data-strat="pmr"` ca proba să o poată număra. */
export function FasiePmrPoly({ poly }: { poly: number[][] }) {
  return (
    <g data-strat="pmr" {...DECOR}>
      <polygon points={poly.map((p) => `${p[0]},${p[1]}`).join(" ")} fill="none"
        stroke={A01.signBlue} strokeWidth={0.05} />
      <path d={caleHasura(liniiHasuraMulti([poly], 0.35, 45))}
        stroke={A01.signBlue} strokeWidth={0.05} fill="none" />
    </g>
  );
}

/** One surface stall: the parked car and, for an accessible bay, the blue plate.
 *
 *  The car is drawn NOSE-IN — front toward the back of the stall, away from the
 *  aisle — which is how the reference parks every one of its 236 cars, and the
 *  opposite of `glyphs.Bay`'s basement convention. `#pg-car` is line art with no
 *  body fill, so a light rounded rect goes under it; on `#7c7c7c` asphalt an
 *  unfilled outline all but disappears. */
export function SiteBay({ s, pick, selected }: { s: SiteStall } & Pickable) {
  const W = s.w, D = s.d;
  const ada = s.kind === "ada";
  // LOCUL PMR LĂRGIT (3.18, fel `ada`, 3,70 m): mașina stă pe un loc obișnuit
  // de 2,50 m, iar restul lățimii e fâșia de transfer (1,20 m), hașurată, pe
  // aceeași parte în reperul locului (+x local) — nu o mașină lată de 3,70 m
  // (Oana, 30.10.2026). Un loc `ada` de lățime obișnuită (fâșia e obiect
  // separat, `AdaAisle`, ca la subsol) rămâne cum era.
  const fasie = ada && W > SITE_STALL_W + 0.6 ? W - SITE_STALL_W : 0;
  const Wm = W - fasie;                       // lățimea pe care stă mașina
  const ox = -fasie / 2;                      // mașina trasă spre partea fără fâșie
  // The car is inset from the bay rather than sized to it: the reference leaves
  // a good third of a metre of asphalt showing either side, and that margin is
  // what makes a row read as parked cars rather than as paving slabs.
  const bw = Math.max(1.2, Wm - 0.40), bd = Math.max(2.4, D - 0.44);
  return (
    <g transform={`translate(${s.cx} ${s.cy}) rotate(${s.deg})`}
       data-pick={pick?.["data-pick"]} style={pick?.style}>
      {/* pick target: the whole bay, before the paint */}
      <rect x={-W / 2} y={-D / 2} width={W} height={D} fill="transparent" />
      {/* Nose-IN — the front toward the back of the bay, away from the aisle —
          which is how the reference parks every one of its 236 cars, and the
          opposite of `glyphs.Bay`'s basement convention. `#pg-car` is potrace
          line art with no body fill: scaled into a 4 m box its strokes go
          sub-pixel, so the silhouette needs a filled body under it or it
          disappears against the apron. */}
      <g transform={`translate(${ox} 0) rotate(180)`}>
        {/* O SINGURĂ MAȘINĂ (Oana, 31.10.2026): desenul fin `#pg-car` stins peste siluetă
            avea alt contur decât ea și se citea ca a doua mașină, una peste alta. Silueta
            desenată (caroserie, parbriz, lunetă, oglinzi) ține singură la orice zoom. */}
        <CarBody w={bw} d={bd} ada={ada} fill={culoareMasina(s.cx, s.cy)} />
      </g>
      {fasie > 0 && <FasiePmr x0={W / 2 - fasie} x1={W / 2} D={D} />}
      {/* LOC ROȘU (3.18): agentul l-a desenat, dar încalcă o regulă
          (`site_plan.loc.valid = false`). Umplutură roșie translucidă peste
          mașină, ca pe planșa SVG a agentului: se vede locul, și se vede că nu
          se pune. Un loc fără câmp rămâne cum era. */}
      {s.valid === false && (
        <rect x={-W / 2} y={-D / 2} width={W} height={D}
          fill={A01.red} fillOpacity={0.35} stroke={A01.red} strokeWidth={0.08} />
      )}
      {ada && (
        <g transform={`translate(${ox} ${-D / 2 + 0.85})`}>
          <rect x={-0.62} y={-0.62} width={1.24} height={1.24} rx={0.1}
            fill={A01.signBlue} />
          <g fill="#ffffff">
            <circle cx={0.02} cy={-0.30} r={0.13} />
            <path d="M -0.24 0.42 L -0.04 -0.06 L 0.26 -0.06" fill="none"
              stroke="#ffffff" strokeWidth={0.11}
              strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={0.0} cy={0.26} r={0.26} fill="none"
              stroke="#ffffff" strokeWidth={0.11} />
          </g>
        </g>
      )}
      {selected && (
        <rect x={-W / 2} y={-D / 2} width={W} height={D}
          fill="none" stroke={SEL_STROKE.stroke} strokeWidth={0.22} />
      )}
    </g>
  );
}

/** The stall number: bold black, upright on the bay, as the reference sets it.
 *
 *  The class names the layer. The sheet carries other bare integers — the
 *  boundary vertices are numbered too — so "is there a `>2</text>` in the
 *  markup" cannot tell a stall number from corner 2, which is exactly how the
 *  zoom gate came to be tested by something it does not control. */
export function SiteStallNumber({ s, n }: { s: SiteStall; n: number }) {
  const turn = useViewTurn();
  const fs = 1.05;
  const label = String(n);
  const w = label.length * fs * 0.64 + 0.26, h = fs * 1.06;
  return (
    // UPRIGHT ON THE SHEET, not turned with the bay: the reference reads every
    // number horizontally, whatever way the row runs.
    <g className="pg-stall-no"
       transform={`translate(${s.cx} ${s.cy}) rotate(${-turn})`} {...DECOR}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} fill="#ffffff" />
      {/* the tick either side, which is what makes it read as a bay's number
          rather than as a number lying on a car */}
      <path d={`M${-w / 2 - 0.30} 0h0.22M${w / 2 + 0.08} 0h0.22`}
        stroke={A01.ink} strokeWidth={0.09} />
      <text x={0} y={CAP(fs)} fontSize={fs} fontFamily={SANS} fontWeight={700}
        textAnchor="middle" fill={A01.ink}>{label}</text>
    </g>
  );
}

// --------------------------------------------------------------------------- //
// road furniture
// --------------------------------------------------------------------------- //

/** A traffic sign on its white backing plate, rotated with the road it governs.
 *  Drawn at a fixed 1.9 m so it stays a symbol rather than scaling into a
 *  billboard — exactly how the reference sizes them. */
export function SignPlate({ s }: { s: SiteSign }) {
  const R = 0.95, t = R * 0.78;
  const tri = (down: boolean) => {
    const k = down ? -1 : 1;
    return `${0},${k * -t} ${t * 0.92},${k * t * 0.62} ${-t * 0.92},${k * t * 0.62}`;
  };
  return (
    <g transform={`translate(${s.x} ${s.y}) rotate(${s.deg})`} {...DECOR}>
      <rect x={-R} y={-R} width={2 * R} height={2 * R} fill="#ffffff" />
      {s.kind === "yield" && (
        <>
          <polygon points={tri(true)} fill={A01.signRed} />
          <polygon points={tri(true)} fill="#ffffff" transform="scale(0.52)" />
        </>
      )}
      {s.kind === "danger" && (
        <>
          <polygon points={tri(false)} fill={A01.signRed} />
          <polygon points={tri(false)} fill="#ffffff" transform="scale(0.52)" />
        </>
      )}
      {s.kind === "noEntry" && (
        <>
          <circle cx={0} cy={0} r={t * 0.86} fill={A01.signRed} />
          <rect x={-t * 0.52} y={-t * 0.17} width={t * 1.04} height={t * 0.34}
            fill="#ffffff" />
        </>
      )}
      {s.kind === "ahead" && (
        <>
          <circle cx={0} cy={0} r={t * 0.86} fill={A01.signBlue} />
          <path d={`M0 ${t * 0.46} L0 ${-t * 0.32} M0 ${-t * 0.5} l${-t * 0.24} ${t * 0.28} M0 ${-t * 0.5} l${t * 0.24} ${t * 0.28}`}
            fill="none" stroke="#ffffff" strokeWidth={t * 0.18}
            strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </g>
  );
}

/** A painted direction arrow: white paint on a dark plate of asphalt, which is
 *  how the reference draws every one of them.
 *
 *  Two shapes, measured off the sheet: a straight arrow whose head is about
 *  twice the width of its shaft, and a left-turn arrow whose shaft runs down
 *  from the top and hooks left into a head at the bottom. Both point along -y,
 *  the outward heading, so the glyph's `deg` is the direction of travel. */
export function RoadArrowMark({ m }: { m: RoadMark }) {
  const W = 1.2, H = 2.55;
  const t = H / 2, b = -H / 2;                       // plate ends (y grows down)
  const shaft = W * 0.17, head = W * 0.36;
  return (
    <g transform={`translate(${m.x} ${m.y}) rotate(${m.deg})`} {...DECOR}>
      <rect x={-W / 2} y={b} width={W} height={H} fill={A01.apron} />
      <rect x={-W / 2} y={b} width={W} height={H} fill="url(#pg-site-tarmac)" />
      {m.kind === "straight" ? (
        <path d={`M0 ${b + 0.16}
                  L${head} ${b + 0.92} L${shaft} ${b + 0.92}
                  L${shaft} ${t - 0.18} L${-shaft} ${t - 0.18}
                  L${-shaft} ${b + 0.92} L${-head} ${b + 0.92} Z`}
          fill="#ffffff" />
      ) : (
        // the hook: down the plate, then left into the head
        <path d={`M${shaft * 1.1} ${b + 0.16}
                  L${shaft * 1.1} ${t - 0.92}
                  L${-W * 0.10} ${t - 0.92} L${-W * 0.10} ${t - 0.62}
                  L${-W * 0.40} ${t - 1.08} L${-W * 0.10} ${t - 1.54}
                  L${-W * 0.10} ${t - 1.24} L${-shaft * 1.1} ${t - 1.24}
                  L${-shaft * 1.1} ${b + 0.16} Z`}
          fill="#ffffff" />
      )}
    </g>
  );
}

/** The lane divider on a two-way road: black dashes down the centreline, as the
 *  reference paints between its opposing arrows. Takes a PATH, not a segment:
 *  the centreline stops at the road it ends in and bends round a corner
 *  (`carosabil.ts`). */
export function LaneDashes({ d }: { d: string }) {
  return (
    <path d={d} fill="none" stroke={A01.ink}
      strokeWidth={0.14} strokeDasharray="1.6 1.6" opacity={0.75} {...DECOR} />
  );
}

/** A vehicle ramp, annotated the way the reference annotates one.
 *
 *  It is NOT drawn as a thing apart: same grey as the carriageway, thin dark
 *  edge, and a dashed centreline running down it — on the sheet a ramp reads as
 *  the road continuing and tipping over, which is what it is.
 *
 *  What says «it goes DOWN» is the centreline itself: its dashes shorten
 *  toward the deep end and the line fades as it goes, the way a line does when
 *  it runs into shadow. Nothing else is added — no cross lines, no darkened
 *  fill — so the ramp stays one item among many on the sheet; only the deep
 *  end is closed by a thin solid bar, the slab edge it passes under. Variant
 *  C4 on the design canvas «Reprezentarea rampei pe A01», chosen over a
 *  darkening fill (D, too loud) and cross lines (C, fight the stall marks).
 *
 *  The grade is written twice, once as a percentage and once in degrees, both
 *  turned to read up the ramp and set side by side rather than stacked, with an
 *  arrow beside them pointing the way it climbs. Degrees are whole numbers: the
 *  sheet writes "10°", not "10.20°" — two decimals on a figure that is `atan` of
 *  a rounded percentage is precision the input never had.
 *
 *  The MOUTH — the first metres at 5 % from the kerb — is marked separately,
 *  by `RampMouthMark`, ON TOP of the carriageway: see there. */
export function RampMark({ r, showLabel }: { r: SiteRamp; showLabel: boolean }) {
  const turn = useViewTurn();
  const pct = r.slope === undefined ? null : Math.round(r.slope * 100);
  const deg = r.slope === undefined ? null
    : Math.round((Math.atan(r.slope) * 180) / Math.PI);
  const fs = Math.max(0.55, Math.min(1.15, r.w * 0.27));
  // Along the ramp's own axis, mouth to deep end: a straight chord on a
  // straight ramp, the tangent-arc-straight polyline on a turned one.
  const path = r.path?.length >= 2 ? r.path : [r.b, r.a];
  let run = 0;
  for (let i = 1; i < path.length; i++) run += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
  const arrow = Math.min(run * 0.28, fs * 5.5);
  // The dashes: 1,5 m on, 1,1 m off at the mouth, each pair 14 % shorter than
  // the last, never under 0,33 / 0,22 m — the ramp the canvas shows.
  const dashes: [number, number][] = [];
  for (let t = 0, d = 1.5, g = 1.1; t + d <= run - 0.4 && dashes.length < 80;
       t += d + g, d = Math.max(0.33, d * 0.86), g = Math.max(0.22, g * 0.86))
    dashes.push([t, t + d]);
  // One gradient per DRAWING of the ramp: the same document is drawn more than
  // once on a page (preview, viewer), and two `<defs>` with one id would leave
  // every copy pointing at whichever came first.
  const fade = `rampa-ax-${useId()}`;
  // The deep-end wall: the edge of the outline nearest the deep end.
  const wallW = Math.min(0.33, r.w * 0.06);
  const [w1, w2] = r.deep ?? [r.wall[1], r.wall[2]];
  return (
    <g {...DECOR}>
      {/* Fill and outline are SEPARATE, and the outline is open: the ramp keeps
          its two retaining walls but nothing closes the mouth, so the road runs
          straight into it. Drawn before the carriageway, so the stretch of wall
          that reaches into the road is painted over by it — which is what puts
          the walls at the kerb without needing to know where the kerb is. */}
      <polygon points={r.poly.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill={A01.road} />
      <polyline points={r.wall.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill="none" stroke={A01.ink} strokeWidth={0.1} strokeLinejoin="round" />
      <line x1={w1[0]} y1={w1[1]} x2={w2[0]} y2={w2[1]}
        stroke={A01.ink} strokeWidth={wallW} strokeLinecap="square" />
      <defs>
        {/* In world units, mouth to deep end, so it turns with the ramp and
            with the view. */}
        <linearGradient id={fade} gradientUnits="userSpaceOnUse"
          x1={r.b[0]} y1={r.b[1]} x2={r.a[0]} y2={r.a[1]}>
          <stop offset="0" stopColor={A01.ink} stopOpacity={1} />
          <stop offset="0.55" stopColor={A01.ink} stopOpacity={0.65} />
          <stop offset="1" stopColor={A01.ink} stopOpacity={0.08} />
        </linearGradient>
      </defs>
      <g stroke={`url(#${fade})`} strokeWidth={0.14}>
        {dashes.map(([t0, t1], i) => {
          const p0 = pathAt(path, t0).p, p1 = pathAt(path, t1).p;
          return <line key={i} x1={p0[0]} y1={p0[1]} x2={p1[0]} y2={p1[1]} />;
        })}
      </g>

      {showLabel && pct !== null && (
        <>
          {/* The grade is STACKED and upright, not set along the ramp.
              The sheet sets the two figures side by side across the ramp, which
              works because a sheet never turns. Here they do turn, and a layout
              measured across the ramp puts the two lines broadside once the text
              is levelled — they ran into each other and read as one number. Two
              lines about a single anchor cannot. */}
          <g transform={`translate(${r.cx} ${r.cy}) rotate(${-turn})`}>
            <text x={0} y={-fs * 0.15} fontSize={fs} fontFamily={SANS}
              fontWeight={700} textAnchor="middle" fill={A01.ink}>{pct}%</text>
            <text x={0} y={fs * 1.05} fontSize={fs} fontFamily={SANS}
              fontWeight={700} textAnchor="middle" fill={A01.ink}>{deg}&#176;</text>
          </g>
          {/* The climb is a DIRECTION, so it turns with the ramp. `r.up` and not
              the sign of `deg`: `deg` has already been turned to keep the text
              readable, so reading the direction off it sends the arrow downhill
              on half the ramps. */}
          <g transform={`translate(${r.cx} ${r.cy}) rotate(${r.deg}) `
                        + `translate(0 ${r.w * 0.30}) scale(${r.up} 1)`}>
            <line x1={-arrow / 2} y1={0} x2={arrow * 0.34} y2={0}
              stroke={A01.ink} strokeWidth={fs * 0.11} />
            <polygon fill={A01.ink}
              points={`${arrow / 2},0 ${arrow * 0.3},${fs * 0.36} ${arrow * 0.3},${-fs * 0.36}`} />
          </g>
        </>
      )}
    </g>
  );
}

/** The MOUTH of a ramp — the first metres at 5 % from the kerb, before the
 *  real grade — marked the way the -1 sheet marks it (`AccessRampView`): a
 *  dashed edge across the ramp where the grade changes and «5%» on the stretch
 *  before it. The rule (6 m at 5 %) is the architecture's; the site plan only
 *  shows it (Oana, 29.09.2026).
 *
 *  Drawn ON TOP of the carriageway, not with `RampMark` underneath it: the
 *  waiting apron may sit over the mouth (faza 5, R1: no room outside it), and
 *  under the road the mark disappeared exactly where it mattered. */
export function RampMouthMark({ r, showLabel }: { r: SiteRamp; showLabel: boolean }) {
  const turn = useViewTurn();
  const run = Math.hypot(r.b[0] - r.a[0], r.b[1] - r.a[1]);
  if (!(r.gura > 0.5 && r.gura < run - 0.5)) return null;
  const fs = Math.max(0.55, Math.min(1.15, r.w * 0.27));
  // Down the ramp at the mouth: the first leg of its axis (on a turned ramp
  // the chord to the deep end points somewhere else).
  const [ux, uy] = r.u0 ?? [(r.a[0] - r.b[0]) / run, (r.a[1] - r.b[1]) / run];
  const mx = r.b[0] + ux * (r.gura / 2), my = r.b[1] + uy * (r.gura / 2);
  return (
    <g {...DECOR}>
      {/* the edge between the 5 % stretch and the grade, across the ramp */}
      <line x1={r.b[0] + ux * r.gura - uy * (r.w / 2)} y1={r.b[1] + uy * r.gura + ux * (r.w / 2)}
            x2={r.b[0] + ux * r.gura + uy * (r.w / 2)} y2={r.b[1] + uy * r.gura - ux * (r.w / 2)}
            stroke={A01.ink} strokeWidth={0.1} strokeDasharray="0.5 0.35" />
      {showLabel && (
        <text x={mx} y={my} fontSize={fs * 0.7} fontFamily={SANS} fontWeight={700}
          textAnchor="middle" dominantBaseline="central" fill={A01.ink}
          transform={`rotate(${-turn} ${mx} ${my})`}>
          5%
        </text>
      )}
    </g>
  );
}

/** A pedestrian crossing: the painted bars, at the street access. */
export function ZebraMark({ c }: { c: Crossing }) {
  const bars = Math.max(3, Math.floor(c.w / 0.9));
  const pitch = c.w / bars;
  return (
    <g transform={`translate(${c.x} ${c.y}) rotate(${c.deg})`} {...DECOR}>
      {Array.from({ length: bars }, (_, i) => (
        <rect key={i} x={-c.w / 2 + i * pitch + pitch * 0.22} y={-c.len / 2}
          width={pitch * 0.56} height={c.len}
          fill="#ffffff" stroke={A01.ink} strokeWidth={0.04} />
      ))}
    </g>
  );
}

// --------------------------------------------------------------------------- //
// sheet furniture
// --------------------------------------------------------------------------- //

/** A dimension: the run, a tick dot at each end, and the value on its plate —
 *  in centimetres, unsuffixed, the way the reference writes every one. */
export function DimSpanView({ d, fs = 0.95 }: { d: DimSpan; fs?: number }) {
  const turn = useViewTurn();
  const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const mx = (d.x1 + d.x2) / 2, my = (d.y1 + d.y2) / 2;
  let a = (Math.atan2(dy, dx) * 180) / Math.PI;
  // Upright ON SCREEN, so the turn counts: a chain that reads left-to-right at
  // rest reads right-to-left once the plan is past a quarter turn, and the flip
  // has to know that. `Plate` then takes the view's turn back out again.
  const screen = ((a + turn + 180) % 360 + 360) % 360 - 180;
  if (screen > 90 || screen < -90) a += 180;
  // STILUL COTEI DE PLANȘĂ, același pe toate cotele planului de situație:
  // linia trece cu `PESTE` de capete, capetele sunt tăieturi la 45°, iar când
  // punctele măsurate stau departe de linie, liniile ajutătoare pleacă din ele
  // cu un `GOL` mic și trec și ele cu `PESTE` de linia de cotă. Era un punct
  // roșu la fiecare capăt, care pe un lanț de locuri de parcare nu mai spunea
  // unde se termină o cotă și unde începe următoarea.
  const PESTE = 0.6, GOL = 0.3, TAIE = 0.45;
  const ajutatoare = ([f, [x, y]]: [Pt, Pt], i: number) => {
    const ex = x - f[0], ey = y - f[1];
    const el = Math.hypot(ex, ey);
    if (el < 2 * GOL) return null;
    const px = ex / el, py = ey / el;
    return (
      <line key={i} x1={f[0] + px * GOL} y1={f[1] + py * GOL}
            x2={x + px * PESTE} y2={y + py * PESTE}
            stroke={A01.red} strokeWidth={0.05} />
    );
  };
  // Tăietura, la 45° între linia de cotă și normala ei.
  const nx = -uy, ny = ux;
  const tx = (ux + nx) * TAIE, ty = (uy + ny) * TAIE;
  const taietura = (x: number, y: number) => (
    <line x1={x - tx} y1={y - ty} x2={x + tx} y2={y + ty}
          stroke={A01.red} strokeWidth={0.14} />
  );
  return (
    <g {...DECOR}>
      {d.ex1 && ajutatoare([d.ex1, [d.x1, d.y1]], 1)}
      {d.ex2 && ajutatoare([d.ex2, [d.x2, d.y2]], 2)}
      <line x1={d.x1 - ux * PESTE} y1={d.y1 - uy * PESTE}
            x2={d.x2 + ux * PESTE} y2={d.y2 + uy * PESTE}
            stroke={A01.red} strokeWidth={0.07} />
      {taietura(d.x1, d.y1)}
      {taietura(d.x2, d.y2)}
      <Plate x={mx} y={my} text={d.label} fs={fs} deg={a} />
    </g>
  );
}

/** Lungimea sau lățimea unui corp: aceeași cotă de planșă, cu cuvântul în
 *  față («lungime 47,30 m») — cerută «clar». Cuvântul cade când latura e prea
 *  scurtă să-l țină: rămâne cifra, care e ce se citește. */
export function CotaCorpView({ c, fs = 1.0 }: { c: CotaCorp; fs?: number }) {
  const len = Math.hypot(c.b[0] - c.a[0], c.b[1] - c.a[1]);
  const lung = t(c.fel === "lungime" ? "a01.cota_lungime" : "a01.cota_latime",
                 { m: c.m });
  const label = lung.length * fs * 0.60 + fs * 2 < len ? lung : `${c.m} m`;
  return <DimSpanView fs={fs} d={{ x1: c.a[0], y1: c.a[1], x2: c.b[0], y2: c.b[1],
                                   label, ex1: c.pa, ex2: c.pb }} />;
}

/** A numbered boundary corner, ringed the way a survey ring is annotated. */
export function VertexMark({ v }: { v: Vertex }) {
  const turn = useViewTurn();
  const fs = 1.2;
  return (
    <g {...DECOR}>
      <circle cx={v.x} cy={v.y} r={1.5} fill="none"
        stroke={A01.red} strokeWidth={0.14} />
      <g transform={`rotate(${-turn} ${v.x} ${v.y})`}>
        <text x={v.x + 2.6} y={v.y + CAP(fs)} fontSize={fs} fontFamily={SANS}
          fontWeight={700} textAnchor="middle" fill={A01.ink}>{v.i}</text>
      </g>
    </g>
  );
}

/** The compass rose: an eight-point star with alternating light and dark halves
 *  and the cardinal letters, under a bold N. */
export function CompassRose({ x, y, r, title = true }:
  { x: number; y: number; r: number;
    /** The big "N" set above the star. It belongs on a plotted sheet, where
     *  there is room for it. In a small widget that TURNS it is the worst thing
     *  on the glyph: it sits 1.75 r out, so it sweeps a circle half again as
     *  wide as the star and is the first thing to be clipped — and it only
     *  repeats what the star's own "n" already says. */
    title?: boolean }) {
  const pts8: string[] = [];
  for (let k = 0; k < 8; k++) {
    const a = (k * Math.PI) / 4 - Math.PI / 2;
    const rr = k % 2 === 0 ? r : r * 0.72;
    const wa = Math.PI / 8;
    const tip = [Math.cos(a) * rr, Math.sin(a) * rr];
    const l = [Math.cos(a - wa) * rr * 0.30, Math.sin(a - wa) * rr * 0.30];
    const g = [Math.cos(a + wa) * rr * 0.30, Math.sin(a + wa) * rr * 0.30];
    pts8.push(`M0 0L${l[0]} ${l[1]}L${tip[0]} ${tip[1]}Z`);
    pts8.push(`M0 0L${tip[0]} ${tip[1]}L${g[0]} ${g[1]}Z`);
  }
  const fs = r * 0.42;
  return (
    <g {...DECOR}>
      {title && (
        <text x={x} y={y - r * 1.75} fontSize={r * 1.05} fontFamily={SANS}
          fontWeight={800} textAnchor="middle" fill={A01.ink}>N</text>
      )}
      <g transform={`translate(${x} ${y})`}>
        {pts8.map((d, i) => (
          <path key={i} d={d} fill={i % 2 === 0 ? "#ffffff" : A01.ink}
            stroke={A01.ink} strokeWidth={r * 0.018} />
        ))}
        {([["n", 0, -1], ["e", 1, 0], ["s", 0, 1], ["w", -1, 0]] as const).map(
          ([t, ux, uy]) => (
            <text key={t} x={ux * r * 1.34} y={uy * r * 1.34 + CAP(fs)}
              fontSize={fs} fontFamily="Georgia, 'Times New Roman', serif"
              textAnchor="middle" fill={A01.ink}>{t}</text>
          ))}
      </g>
    </g>
  );
}

/** The green-areas balance, set the way the reference sets it: heavy outer
 *  frame, hairline rules, bold italic caps, values in m² and per cent of the
 *  plot. The numbers are the producer's own measured areas, not
 *  anything measured off the drawing. */
/** Înălțimea unui rând și aerul din jurul tabelului, în metri de desen. Scrise
 *  aici fiindcă tot aici se decide câte rânduri are legenda: cine o încadrează
 *  trebuie să afle înălțimea DIN ea, nu s-o ghicească. Un `viewBox` fix era
 *  bun cât timp rândurile erau patru — la al cincilea, tabelul a ieșit tăcut
 *  din cadru. */
export const BILANT_RH = 2.1;
export const BILANT_AER = 3.6;

/** Rândurile bilanțului. Exportate ca să poată fi NUMĂRATE fără să fie
 *  desenate — altfel înălțimea casetei rămâne o constantă care se strică la
 *  fiecare rând nou. */
export type Indicatori = {
  pot?: { valoare?: number; limita?: number };
  cut?: { valoare?: number; limita?: number };
  niveluri?: { valoare?: number; limita?: number; regim?: string };
  /** Desfășurata construită, în m². Supraterana e ce intră la C.U.T.; subterana
   *  e scrisă separat fiindcă e cealaltă jumătate a întrebării «cât
   *  construiesc», iar regimul o pune la C.U.T. abia uneori. */
  supraterana?: number;
  subterana?: number;
};

/** „12.345 mp" — mii despărțite, ca în tabelul de suprafețe de dedesubt. */
const mp = (v?: number) =>
  v === undefined || v === null ? "—"
    : `${Math.round(v).toLocaleString("ro-RO")} mp`;

/** „0,18" — virgulă zecimală, ca pe planșele româneşti. */
const zec = (v?: number, n = 2) =>
  v === undefined || v === null ? "—" : v.toFixed(n).replace(".", ",");

export function randuriBilant(areas: Record<string, number>):
    { name: string; v?: number; p?: string; c?: string }[] {
  const total = areas?.green_total
    ?? ((areas?.deep ?? 0) + (areas?.over_slab ?? 0));
  // Pastila de culoare e ce transformă tabelul din listă de cifre în LEGENDĂ:
  // fără ea, cine se uită la planșă trebuie să ghicească ce înseamnă fiecare
  // suprafață colorată. Rândurile fără culoare (terenul, totalul) sunt sume, nu
  // suprafețe desenate — de aceea rămân fără pastilă, în loc să primească una
  // inventată.
  return [
    { name: t("a01.suprafata_teren"), v: areas?.lot, p: "100%" },
    { name: t("a01.total_verde"), v: total },
    { name: t("a01.verde_sol"), v: areas?.deep, c: A01.deep },
    { name: t("a01.verde_subsol"), v: areas?.over_slab, c: A01.slab },
    // Aceeași culoare ca verdele peste subsol, și pe bună dreptate: plantarea
    // de peste subsol STĂ pe săpătură. Se deosebesc prin textură — săpătura
    // neacoperită rămâne netedă, plantarea primește iarbă deasupra.
    { name: t("a01.sapatura"), v: areas?.excavation, c: A01.slab },
  ];
}

/** Câte rânduri de indicatori are documentul (0, dacă n-a trecut prin verificări). */
export function randuriIndicatori(ind?: Indicatori):
    { name: string; v: string; lim: string }[] {
  if (!ind) return [];
  const lim = (x?: number, n = 2, u = "") =>
    x === undefined ? "—" : `max ${zec(x, n)}${u}`;
  return [
    ...(ind.pot ? [{ name: "P.O.T.", v: zec(ind.pot.valoare),
                     lim: lim(ind.pot.limita) }] : []),
    ...(ind.cut ? [{ name: "C.U.T.", v: zec(ind.cut.valoare),
                     lim: lim(ind.cut.limita) }] : []),
    // ARIILE CONSTRUITE, sub indicatorii de care țin.
    //
    // P.O.T. și C.U.T. sunt RAPOARTE — spun cât din teren, nu cât în metri
    // pătrați — iar omul care se uită la planșă vrea și cifra: ce vinde e aria,
    // nu raportul. Scrise în metri pătrați, fără limită alături: limita lor e
    // chiar C.U.T.-ul de deasupra, iar repetată aici ar fi aceeași regulă
    // scrisă de două ori.
    ...(ind.supraterana !== undefined ? [{
      name: "S. CONSTRUITA SUPRATERANA",
      v: mp(ind.supraterana), lim: "",
    }] : []),
    ...(ind.subterana !== undefined ? [{
      name: "S. CONSTRUITA SUBTERANA",
      v: mp(ind.subterana), lim: "",
    }] : []),
    ...(ind.niveluri ? [{
      name: "REGIM DE INALTIME",
      // Textul motorului de reguli („P+14"), nu o reconstrucție din numărul de
      // niveluri: regimul se scrie altfel cu demisol, mansardă sau retrageri.
      v: ind.niveluri.regim || zec(ind.niveluri.valoare, 0),
      lim: lim(ind.niveluri.limita, 0, " niv"),
    }] : []),
  ];
}

// Marginea de SUS a cutiei depinde de ce e primul lucru desenat. Cu titlul
// deasupra (corp 1,35, așezat la `-1.0`) trebuie loc cât el; cu indicatorii
// deasupra, primul rând începe aproape de `0` și aceeași margine lasă o bandă
// goală între rigla de scară și P.O.T. — exact golul care se vedea.
const SUS_TITLU = -2.8;
const SUS_IND = -0.9;
const JOS = 0.8;

/** `viewBox`-ul cutiei, ca desenul și cadrul lui să nu se poată despărți. */
export function cutiaBilant(areas: Record<string, number>, ind?: Indicatori,
                            w = 36) {
  const sus = randuriIndicatori(ind).length ? SUS_IND : SUS_TITLU;
  const h = inaltimeIndicatori(ind) + inaltimeTabel(areas) + JOS - sus;
  return { viewBox: `-0.6 ${sus} ${w} ${h}`, h, sus };
}

/** Înălțimea totală a legendei — indicatorii deasupra, apoi titlul și tabelul. */
export const inaltimeBilant = (areas: Record<string, number>, ind?: Indicatori) =>
  cutiaBilant(areas, ind).h;

/** Cât ocupă blocul de deasupra. Zero fără indicatori: legenda arată atunci
 *  EXACT ca înainte, până la ultimul pixel. */
export const inaltimeIndicatori = (ind?: Indicatori) => {
  const n = randuriIndicatori(ind).length;
  // 3,0 nu 1,4: titlul legendei are corp 1,35 și, cu spațiul vechi,
  // cădea peste ultimul rând de indicatori.
  return n ? BILANT_RH * n + 3.0 : 0;
};

// LĂȚIMEA UNUI TEXT, aproximată. SVG-ul n-are rupere automată de rând, iar
// măsurarea adevărată ar cere DOM — pe care desenul ăsta nu-l are când i se
// calculează înălțimea. Factorul e potrivit pe fontul cutiei (sans bold italic,
// majuscule): 0,58 din corp per caracter, generos, ca o estimare greșită să
// rupă un rând în plus, nu să lase textul peste coloana de cifre.
const latime = (t: string, fs: number) => t.length * fs * 0.58;

/** Numele rândului, rupt în cel mult două rânduri dacă nu încape.
 *
 *  Se rupe la SPAȚIU, niciodată în mijlocul unui cuvânt, și se alege spațiul
 *  care lasă rândurile cât mai egale — „SPATIU VERDE PESTE / SUBSOL PROPUS", nu
 *  „SPATIU / VERDE PESTE SUBSOL PROPUS".
 */
export function rupeNumele(nume: string, latDisp: number, fs: number): string[] {
  if (latime(nume, fs) <= latDisp) return [nume];
  const cuvinte = nume.split(" ");
  if (cuvinte.length < 2) return [nume];       // un cuvânt nu se taie
  let cel_mai_bun: string[] | null = null;
  let diferenta = Infinity;
  for (let i = 1; i < cuvinte.length; i++) {
    const a = cuvinte.slice(0, i).join(" "), b = cuvinte.slice(i).join(" ");
    const la = latime(a, fs), lb = latime(b, fs);
    if (la > latDisp || lb > latDisp) continue;
    const d = Math.abs(la - lb);
    if (d < diferenta) { diferenta = d; cel_mai_bun = [a, b]; }
  }
  // Dacă nici rupt în două nu încape, se lasă întreg: mai bine un rând lung
  // decât trei rânduri care ies din cutie.
  return cel_mai_bun ?? [nume];
}

/** Înălțimile rândurilor tabelului, în ordine — un rând rupt e mai înalt. */
export function inaltimiRanduri(areas: Record<string, number>, w = 34): number[] {
  const fs = 1.05, C1 = w - 11;
  return randuriBilant(areas).map((r) => {
    const stanga = r.c ? 3.0 : 0.9;
    const linii = rupeNumele(r.name, C1 - stanga - 1.2, fs);
    return linii.length > 1 ? BILANT_RH * 1.5 : BILANT_RH;
  });
}

export const inaltimeTabel = (areas: Record<string, number>, w = 34) =>
  inaltimiRanduri(areas, w).reduce((t, h) => t + h, 0);


export function GreenBalance({ areas, indicatori, x, y, w = 34 }:
  { areas: Record<string, number>; indicatori?: Indicatori;
    x: number; y: number; w?: number }) {
  const lot = areas?.lot ?? 0;
  const fmt = (v?: number) => v === undefined ? "—" : `${Math.round(v)}mp`;
  const pct = (v?: number) => (v === undefined || lot <= 0) ? "—"
    : `~${Math.round((v / lot) * 100)}%`;
  const rows = randuriBilant(areas);
  const RH = BILANT_RH, fs = 1.05;
  // Rândurile NU mai sunt egale: unul cu numele rupt în două e mai înalt, iar
  // liniile de despărțire trebuie să cadă unde se termină chiar el.
  const hRand = inaltimiRanduri(areas, w);
  const sus = hRand.map((_, i) => hRand.slice(0, i).reduce((t, x) => t + x, 0));
  const H = hRand.reduce((t, x) => t + x, 0);
  const C1 = w - 11, C2 = w - 4.6;          // column rules

  // INDICATORII, deasupra legendei și SEPARAT de ea. Nu sunt suprafețe: n-au
  // pastilă de culoare, n-au procent din lot și n-au ce căuta printre rândurile
  // bilanțului, unde ar fi citite ca încă o hașură de pe planșă. Tabelul de mai
  // jos rămâne exact cel dinainte.
  const ind = randuriIndicatori(indicatori);
  const hInd = inaltimeIndicatori(indicatori);
  const yT = y + hInd;                       // de unde începe legenda propriu-zisă

  return (
    <g fontFamily={SANS} fill={A01.ink} {...DECOR}>
      {ind.length > 0 && (
        <g>
          {ind.map((r, i) => (
            <g key={r.name}>
              <text x={x} y={y + RH * i + RH / 2 + CAP(1.15)} fontSize={1.15}
                fontWeight={700} fontStyle="italic">{r.name}</text>
              <text x={x + w - 9} y={y + RH * i + RH / 2 + CAP(1.15)}
                fontSize={1.15} fontWeight={700} fontStyle="italic"
                textAnchor="end">{r.v}</text>
              <text x={x + w - 0.7} y={y + RH * i + RH / 2 + CAP(1.15)}
                fontSize={1.15} fontWeight={700} fontStyle="italic"
                textAnchor="end" opacity={0.75}>{r.lim}</text>
            </g>
          ))}
        </g>
      )}
      <text x={x} y={yT - 1.0} fontSize={1.35} fontWeight={700}
        fontStyle="italic">LEGENDA SUPRAFETE SPATII VERZI</text>
      <rect x={x} y={yT} width={w} height={H} fill="#ffffff"
        stroke={A01.ink} strokeWidth={0.3} />
      {rows.map((r, i) => (
        <g key={r.name}>
          {i > 0 && (
            <line x1={x} y1={yT + sus[i]} x2={x + w} y2={yT + sus[i]}
              stroke={A01.ink} strokeWidth={0.07} />
          )}
          {(() => {
            const y0 = yT + sus[i], h = hRand[i];
            const stanga = x + (r.c ? 3.0 : 0.9);
            const linii = rupeNumele(r.name, C1 - (r.c ? 3.0 : 0.9) - 1.2, fs);
            // Cifrele stau pe MIJLOCUL rândului, indiferent câte linii are
            // numele: aliniate la prima linie, ar părea agățate de sus.
            const mij = y0 + h / 2 + CAP(fs);
            // Numele rupt se așază simetric față de mijloc.
            const primul = linii.length > 1 ? mij - fs * 0.62 : mij;
            return (
              <>
                {r.c && (
                  <rect x={x + 0.9} y={y0 + h / 2 - 0.62} width={1.5}
                    height={1.24} fill={r.c} stroke={A01.ink} strokeWidth={0.07} />
                )}
                {linii.map((l, j) => (
                  <text key={j} x={stanga} y={primul + j * fs * 1.24}
                    fontSize={fs} fontWeight={700} fontStyle="italic">{l}</text>
                ))}
                <text x={x + C2 - 0.7} y={mij} fontSize={fs}
                  fontWeight={700} fontStyle="italic" textAnchor="end">{fmt(r.v)}</text>
                <text x={x + w - 0.7} y={mij} fontSize={fs}
                  fontWeight={700} fontStyle="italic" textAnchor="end">
                  {r.p ?? pct(r.v)}</text>
              </>
            );
          })()}
        </g>
      ))}
      <line x1={x + C1} y1={yT} x2={x + C1} y2={yT + H} stroke={A01.ink} strokeWidth={0.07} />
      <line x1={x + C2} y1={yT} x2={x + C2} y2={yT + H} stroke={A01.ink} strokeWidth={0.07} />
    </g>
  );
}

// --------------------------------------------------------------------------- //
// porțile și trecerile de pietoni ale planului de situație (3.18, §37)
// --------------------------------------------------------------------------- //

/** POARTA pe limita terenului: un semn mic, de mărimea unei plăcuțe de
 *  circulație (`SignPlate`, 1,9 m), ca să rămână simbol la orice zoom. `deg` e
 *  heading-ul documentului — (sin, −cos), ca la intrările clădirilor — deci
 *  după `rotate(deg)` strada e spre −y. Carosabil: un triunghi plin, cu vârful
 *  spre stradă, în culoarea semnelor; pietonal: un cerc gol. */
export function GateMark({ p }: { p: { x: number; y: number; deg: number; fel: "carosabil" | "pietonal" } }) {
  const R = 0.95, t = R * 0.78;
  const deg = Number.isFinite(p.deg) ? p.deg : 0;
  return (
    <g transform={`translate(${p.x} ${p.y}) rotate(${deg})`} {...DECOR}>
      {p.fel === "pietonal" ? (
        <circle cx={0} cy={0} r={t * 0.72} fill="none" stroke={A01.signRed} strokeWidth={0.22} />
      ) : (
        <polygon points={`0,${-t} ${t * 0.92},${t * 0.62} ${-t * 0.92},${t * 0.62}`}
          fill={A01.signRed} />
      )}
    </g>
  );
}

/** SIMBOLUL unei zone exterioare (Oana, 30.10.2026: «ceva simplu, simbolic»),
 *  desenat în metri, centrat în origine, ~2 m lățime: locul de joacă e un
 *  leagăn (două picioare în A, bara de sus, scaunul pe două sfori), platforma
 *  de gunoi un container cu capac și roți, piațeta o fântână (două cercuri
 *  concentrice și un jet). Linie subțire de cerneală, fără umplutură, ca
 *  simbolurile planșei A01; eticheta stă sub el. */
export function ZonaSimbol({ kind, x, y, turn = 0 }:
  { kind: "playground" | "waste_platform" | "square"; x: number; y: number; turn?: number }) {
  const s = { fill: "none", stroke: A01.ink, strokeWidth: 0.12, strokeLinecap: "round" as const,
              strokeLinejoin: "round" as const };
  return (
    <g transform={`translate(${x} ${y}) rotate(${-turn})`} {...DECOR}>
      {kind === "playground" && (
        <>
          <polyline points="-1.1,0.9 -0.7,-0.9 -0.3,0.9" {...s} />
          <polyline points="0.3,0.9 0.7,-0.9 1.1,0.9" {...s} />
          <line x1={-0.7} y1={-0.9} x2={0.7} y2={-0.9} {...s} />
          <line x1={-0.25} y1={-0.9} x2={-0.25} y2={0.35} {...s} />
          <line x1={0.25} y1={-0.9} x2={0.25} y2={0.35} {...s} />
          <line x1={-0.4} y1={0.35} x2={0.4} y2={0.35} {...s} strokeWidth={0.2} />
        </>
      )}
      {kind === "waste_platform" && (
        <>
          <rect x={-0.9} y={-0.55} width={1.8} height={1.1} rx={0.08} {...s} />
          <rect x={-1.0} y={-0.85} width={2.0} height={0.3} rx={0.08} {...s} />
          <circle cx={-0.55} cy={0.72} r={0.16} {...s} />
          <circle cx={0.55} cy={0.72} r={0.16} {...s} />
          <line x1={-0.3} y1={-0.3} x2={-0.3} y2={0.3} {...s} />
          <line x1={0.3} y1={-0.3} x2={0.3} y2={0.3} {...s} />
        </>
      )}
      {kind === "square" && (
        <>
          <circle cx={0} cy={0.1} r={1.0} {...s} />
          <circle cx={0} cy={0.1} r={0.45} {...s} />
          <line x1={0} y1={0.1} x2={0} y2={-0.9} {...s} />
          <polyline points="-0.35,-0.55 0,-0.9 0.35,-0.55" {...s} />
        </>
      )}
    </g>
  );
}

/** ZEBRA unei treceri de pietoni: dungile albe cu contur gri subțire, gata
 *  calculate de `zebraTrecere` (perpendiculare pe axa trecerii, pe lățimea
 *  ei). Peste asfalt, sub locuri și semne. */
export function ZebraTrecereMark({ dungi }: { dungi: [Pt, Pt, Pt, Pt][] }) {
  return (
    <g {...DECOR}>
      {dungi.map((d, i) => (
        <polygon key={i} points={d.map((q) => `${q[0]},${q[1]}`).join(" ")}
          fill="#ffffff" stroke={A01.hair} strokeWidth={0.03} />
      ))}
    </g>
  );
}
